import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import CustomReCaptcha from "@store/Library/Components/CustomReCaptcha";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    formAction = "formAction",
    submitButtonText = "submitButtonText",
    formElements = "formElements",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.formAction]: string;
        [fields.submitButtonText]: string;
        [fields.formElements]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ExternalContactFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
}

export const externalContactFormStyles: ExternalContactFormStyles = {
    formGridItem: { width: 12 },
    submitButton: {
        css: css`
            margin-top: ${pxToRem(20)};
        `,
    },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
};

const styles = externalContactFormStyles;

const ExternalContactForm = ({ fields, isSignedIn }: Props) => {
    const { formAction, formElements, submitButtonText } = fields;
    const [isSubmitting, setIsSubmitting] = useState(true);
    const toasterContext = useToaster();

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formElement = event.target as HTMLFormElement;
        let validForm = true;
        // eslint-disable-next-line array-callback-return
        Array.from(formElement.elements).map(el => {
            const inputValue = el as HTMLInputElement;
            if (el.hasAttribute("name") && el.hasAttribute("required") && !inputValue.value) {
                validForm = false;
            }
        });

        if (validForm) {
            formElement.submit();
            setIsSubmitting(true);
        } else {
            toasterContext.addToast({
                body: translate("One or more fields are required."),
                messageType: "danger",
            });
        }
    };

    const recaptchaCallBack = () => {
        setIsSubmitting(false);
    };

    useEffect(() => {
        if (isSignedIn) {
            setIsSubmitting(false);
        }
    }, [isSignedIn]);

    const parsedContent = parse(formElements, parserOptions);

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.formGridItem}>
                <form id="externalContactForm" action={formAction} method="POST" onSubmit={handleFormSubmit} noValidate>
                    {parsedContent}
                    <CustomReCaptcha location="ContactUs" customCallBack={recaptchaCallBack} />
                    <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                        {submitButtonText}
                    </Button>
                </form>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ExternalContactForm),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.formAction,
                displayName: "Form Action (url)",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.formElements,
                displayName: "Form Elements",
                tooltip: "exlude form wrapper, submit button and recaptcha",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
            },
        ],
    },
};

export default widgetModule;
